import { Injectable, inject } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { Observable, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UpdateBannerComponent } from '../components/update-banner/update-banner.component';

export type UpdateNoticeKind = 'update' | 'chunk-failed';

/** How many times the notice is held back for a focused text field before it shows anyway. */
export const MAX_DEFERRALS = 5;

/** <input> types where the user types text; checkboxes, radios, buttons, sliders etc. do not count. */
const TEXT_INPUT_TYPES = new Set([
  'text', 'search', 'email', 'number', 'tel', 'url', 'password',
  'date', 'datetime-local', 'month', 'week', 'time',
]);

/** Opens the calm `ri-update-banner` notice. One banner at a time. */
@Injectable({ providedIn: 'root' })
export class UpdateNoticeService {
  private readonly snackBar = inject(MatSnackBar);

  /**
   * Shows the banner (bottom, persistent, non-blocking). `onUpdateNow` runs only when the
   * user presses "Päivitä nyt"; the caller decides how to reload (guarded or not).
   * Dismissal ("Myöhemmin", close, swipe) calls `onDismissed` and does nothing else.
   */
  show(
    kind: UpdateNoticeKind,
    onUpdateNow: () => void,
    onDismissed?: () => void
  ): MatSnackBarRef<UpdateBannerComponent> {
    const ref = this.snackBar.openFromComponent(UpdateBannerComponent, {
      data: {
        bodyKey: kind === 'chunk-failed' ? 'APP_UPDATE.BANNER_BODY_CHUNK_FAILED' : 'APP_UPDATE.BANNER_BODY',
      },
      duration: 0,
      verticalPosition: 'bottom',
      horizontalPosition: this.isPhone() ? 'center' : 'end',
      panelClass: ['ri-update-banner-panel'],
    });
    ref.onAction().subscribe(() => onUpdateNow());
    ref.afterDismissed().subscribe(() => onDismissed?.());
    return ref;
  }

  /**
   * Like show(), but never while the user is typing: if a field has focus, look again after
   * `retryMs` (default one minute) until it does not or `until$` emits (e.g. component destroy).
   * Nothing reloads by itself either way; this is only about not distracting an edit.
   */
  showWhenIdle(
    kind: UpdateNoticeKind,
    onUpdateNow: () => void,
    onDismissed: (() => void) | undefined,
    until$: Observable<unknown>,
    retryMs = 60_000,
    retriesLeft = MAX_DEFERRALS
  ): void {
    // Capped: after MAX_DEFERRALS looks the card is shown anyway (it is non-blocking), so a
    // field that always keeps focus can never hold it back for good.
    if (retriesLeft > 0 && this.isUserEditing()) {
      timer(retryMs)
        .pipe(takeUntil(until$))
        .subscribe(() => this.showWhenIdle(kind, onUpdateNow, onDismissed, until$, retryMs, retriesLeft - 1));
      return;
    }
    this.show(kind, onUpdateNow, onDismissed);
  }

  /**
   * True while the user is typing in a field. The banner is deferred then: it never
   * interrupts an edit (and it never reloads by itself either).
   */
  isUserEditing(): boolean {
    const el = document.activeElement as HTMLElement | null;
    if (!el) return false;
    const tag = el.tagName;
    if (tag === 'INPUT') return TEXT_INPUT_TYPES.has(((el as HTMLInputElement).type || 'text').toLowerCase());
    return tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
  }

  private isPhone(): boolean {
    try {
      return window.matchMedia('(max-width: 600px)').matches;
    } catch {
      return false;
    }
  }
}
